import React from 'react';

import type {
  CodeBlockRedlineInsert,
  CodeBlockRedlineItem,
  ProposalDecisions,
  ProposalSuggestion,
} from '@aline/common';
import { getIsSuggestionReviewable } from '@aline/common';
import { IconButton, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { Check, Crosshair, RefreshCw, Sparkles, SquareCheck, Undo2, X } from 'lucide-react';
import { AlineButton, AlineTooltip } from 'src/components';
import { RedlineActionVisualDiff } from 'src/components/ai/chat/ChatPanelMarkdown/elements/CodeElement/CodeRedlineListElement/RedlineActionVisualDiff';
import { suggestTextStyles } from 'src/utils/preview-styles';
import { makeStyles } from 'tss-react/mui';

import { getPlaybookStrongBorder, PLAYBOOK_TONES } from './playbook-colors';
import type { PlaybookChipTone } from './PlaybookChip';
import { PlaybookChip } from './PlaybookChip';
import { PlaybookExplanationMarkdown } from './PlaybookExplanationMarkdown';
import { usePlaybookNavigate } from './usePlaybookNavigate';

const useStyles = makeStyles()(theme => {
  const hairline = `1px solid ${theme.appColors.rule}`;

  return {
    actionsRow: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    // The resolve buttons close the block; extra air sets them off the cards.
    actionsRowButtons: {
      marginTop: theme.spacing(2),
    },
    block: {
      backgroundColor: theme.appColors.background,
      border: hairline,
      borderRadius: 6,
      // Clipped so a full-bleed suggestion row follows the rounded corners.
      overflow: 'hidden',
      // Horizontal padding lives on inset content so suggestion dividers
      // can span the full card width.
      padding: theme.spacing(1.5, 0),
    },
    blockDimmed: {
      opacity: 0.6,
    },
    /**
     * When suggestions close the card, each row's own padding is the bottom
     * air: the card's would strand the last row's hover short of the edge.
     */
    blockFlushBottom: {
      paddingBottom: 0,
    },
    blockInset: {
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
    },
    label: {
      color: theme.appColors.secondaryType,
      fontSize: 12,
      fontWeight: 600,
    },
    labelRow: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1),
    },
    /** The reference paragraph an insertion is anchored to, muted as context. */
    positionText: {
      color: theme.appColors.iconsType,
      fontSize: 14,
      fontStyle: 'italic',
      whiteSpace: 'pre-wrap',
    },
    previewAnchor: {
      color: theme.appColors.secondaryType,
    },
    previewHtml: {
      fontSize: 14,
    },
    reasoningHeading: {
      alignItems: 'center',
      color: theme.appColors.secondaryType,
      display: 'flex',
      fontSize: 12,
      fontWeight: 600,
      gap: theme.spacing(0.5),
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(2),
    },
    sectionHeading: {
      color: theme.appColors.secondaryType,
      fontSize: 12,
      fontWeight: 600,
    },
    sectionHeadingRow: {
      alignItems: 'center',
      display: 'flex',
      marginTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
    },
    staleBanner: {
      alignItems: 'center',
      backgroundColor: PLAYBOOK_TONES.review.background,
      border: `1px solid ${PLAYBOOK_TONES.review.border}`,
      borderRadius: 6,
      display: 'flex',
      fontSize: 12,
      gap: theme.spacing(1),
      justifyContent: 'space-between',
      marginTop: theme.spacing(1),
      padding: theme.spacing(1, 1.5),
    },
    staleRerun: {
      alignItems: 'center',
      background: 'none',
      border: 'none',
      color: PLAYBOOK_TONES.review.color,
      cursor: 'pointer',
      display: 'flex',
      // The label is the action: it holds one line while the message wraps.
      flexShrink: 0,
      fontSize: 12,
      fontWeight: 600,
      gap: theme.spacing(0.5),
      padding: 0,
      whiteSpace: 'nowrap',
    },
    /**
     * Accept/Reject as one edge-attached control: flush to the card's right
     * border, rounded only on the left, hairline matching the position card.
     */
    suggestionActions: {
      '& > * + *': {
        borderTop: hairline,
      },
      borderBottom: hairline,
      borderLeft: hairline,
      borderRadius: '6px 0 0 6px',
      borderRight: 'none',
      borderTop: hairline,
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      overflow: 'hidden',
    },
    suggestionBody: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      gap: theme.spacing(1),
      minWidth: 0,
    },
    /** Whitespace frames the list; only adjacent edits receive an inset divider. */
    suggestionList: {
      '& > * + *::before': {
        backgroundColor: theme.appColors.rule,
        content: '""',
        height: 1,
        left: theme.spacing(1.5),
        position: 'absolute',
        right: theme.spacing(1.5),
        top: 0,
      },
      marginTop: 0,
    },
    suggestionNoteBody: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 1.5),
    },
    // The text lines up with the position label above it, not with a marker.
    suggestionRow: {
      alignItems: 'flex-start',
      display: 'flex',
      gap: theme.spacing(1),
      padding: theme.spacing(0, 0, 1, 1.5),
    },
    suggestionSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      position: 'relative',
    },
    // Full-bleed hover, so the highlight reads as the edit's whole extent.
    suggestionSectionNavigable: {
      '&:hover': {
        backgroundColor: theme.appColors.darkBackground,
      },
      cursor: 'pointer',
    },
    suggestionTitle: {
      color: theme.appColors.secondaryType,
      fontSize: 12,
      fontWeight: 600,
    },
    text: {
      color: theme.appColors.iconsType,
      fontSize: 14,
      whiteSpace: 'pre-wrap',
    },
  };
});

const useButtonStyles = makeStyles()(theme => {
  return {
    disabled: {
      backgroundColor: PLAYBOOK_TONES.muted.background,
      borderColor: theme.appColors.rule,
      color: PLAYBOOK_TONES.muted.color,
      cursor: 'default',
    },
    icon: {
      height: 12,
      width: 12,
    },
    /** Icon-only variant for the per-suggestion controls. */
    iconButton: {
      '&:disabled': {
        cursor: 'default',
      },
      alignItems: 'center',
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${getPlaybookStrongBorder(theme)}`,
      borderRadius: 6,
      color: theme.appColors.iconsType,
      cursor: 'pointer',
      display: 'inline-flex',
      justifyContent: 'center',
      padding: theme.spacing(0.5),
    },
    // Colored glyph on a neutral border; disabled greys the glyph out too.
    iconButtonAccept: {
      '&:hover:not(:disabled)': {
        backgroundColor: PLAYBOOK_TONES.passed.background,
      },
      '&:not(:disabled)': {
        color: PLAYBOOK_TONES.passed.color,
      },
    },
    iconButtonClusterWrap: {
      display: 'flex',
    },
    /** Nested in the edge-attached Accept/Reject cluster; the group owns the border. */
    iconButtonClustered: {
      border: 'none',
      borderRadius: 0,
      minHeight: 28,
      minWidth: 28,
      width: '100%',
    },
    /** Navigation, not a decision: neutral, so the verdict pair keeps the color. */
    iconButtonJump: {
      '&:hover': {
        backgroundColor: theme.appColors.darkBackground,
      },
      color: theme.appColors.secondaryType,
    },
    iconButtonReject: {
      '&:hover:not(:disabled)': {
        backgroundColor: PLAYBOOK_TONES.failed.background,
      },
      '&:not(:disabled)': {
        color: PLAYBOOK_TONES.failed.color,
      },
    },
    iconButtonSelectedAccept: {
      backgroundColor: PLAYBOOK_TONES.passed.background,
      color: PLAYBOOK_TONES.passed.color,
      cursor: 'default',
    },
    iconButtonSelectedReject: {
      backgroundColor: PLAYBOOK_TONES.failed.background,
      color: PLAYBOOK_TONES.failed.color,
      cursor: 'default',
    },
    /**
     * Compact sizing for the panel: the standard button's 14px/32px reads too
     * large inside a position card. Chained past MuiButton-root so it wins on
     * specificity over the base class.
     */
    resolveButton: {
      '&.MuiButton-root': {
        fontSize: 12,
        height: 26,
        padding: theme.spacing(0.25, 1.25),
      },
    },
    resolveIcon: {
      height: 13,
      width: 13,
    },
    // The same green as the position's Accepted chip: one settled state, one color.
    resolvedLabel: {
      alignItems: 'center',
      color: PLAYBOOK_TONES.passed.color,
      display: 'inline-flex',
      fontSize: 12,
      fontWeight: 600,
      gap: theme.spacing(0.5),
    },
    resolvedRow: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
      justifyContent: 'center',
      width: '100%',
    },
  };
});

/**
 * A resolve action as a standard Aline outlined button (blue border and
 * text), spanning its container. That is a position card for a position's
 * action, and the rule body for an action on the whole rule.
 */
export function ResolveActionButton({
  disabled,
  label,
  onResolve,
}: {
  disabled: boolean;
  label: string;
  onResolve: () => void;
}): JSX.Element {
  const { classes } = useButtonStyles();

  return (
    <AlineButton
      fullWidth
      buttonClassName={classes.resolveButton}
      color="primary"
      disabled={disabled}
      startIcon={<Check className={classes.resolveIcon} />}
      variant="outlined"
      onClick={onResolve}
    >
      {label}
    </AlineButton>
  );
}

/**
 * What a recorded resolution reads as: who resolved it, and a button to take
 * it back. Deliberately not the resolve button in a checked state — a button
 * that says what just happened gives the reader no way to know that clicking
 * it again undoes the decision.
 */
export function ResolvedNotice({
  label,
  onUndo,
  undoDisabled,
}: {
  label: string;
  onUndo: () => void;
  undoDisabled: boolean;
}): JSX.Element {
  const { classes } = useButtonStyles();

  return (
    <div className={classes.resolvedRow}>
      <Typography className={classes.resolvedLabel} component="span" variant="caption">
        <SquareCheck className={classes.resolveIcon} />
        {label}
      </Typography>
      <AlineTooltip arrow placement="left" title="Undo">
        {/* A div so the tooltip still anchors while the button is disabled. */}
        <div>
          <IconButton
            aria-label="Undo"
            disabled={undoDisabled}
            size="small"
            onClick={event => {
              event.stopPropagation();
              onUndo();
            }}
          >
            <Undo2 className={classes.icon} />
          </IconButton>
        </div>
      </AlineTooltip>
    </div>
  );
}

/**
 * The compact per-suggestion Accept / Reject pair: icon-only with a tooltip
 * naming the action, tinting green or red on hover to say which is which.
 */
function SuggestionReviewButton({
  clustered = false,
  disabled,
  icon,
  onClick,
  selected = false,
  tooltip,
}: {
  disabled: boolean;
  icon: 'accept' | 'reject';
  onClick: () => void;
  tooltip: string;
  clustered?: boolean;
  selected?: boolean;
}): JSX.Element {
  const { classes, cx } = useButtonStyles();
  const Icon = icon === 'accept' ? Check : X;

  return (
    <AlineTooltip arrow placement="left" title={tooltip}>
      {/* A div so the tooltip still anchors while the button is disabled. */}
      <div className={clustered ? classes.iconButtonClusterWrap : undefined}>
        <button
          className={cx(
            classes.iconButton,
            clustered && classes.iconButtonClustered,
            icon === 'accept' ? classes.iconButtonAccept : classes.iconButtonReject,
            selected &&
              (icon === 'accept'
                ? classes.iconButtonSelectedAccept
                : classes.iconButtonSelectedReject),
            disabled && !selected && classes.disabled
          )}
          disabled={disabled || selected}
          type="button"
          onClick={event => {
            event.stopPropagation();
            onClick();
          }}
        >
          <Icon className={classes.icon} />
        </button>
      </div>
    </AlineTooltip>
  );
}

/**
 * Locating the edit in the document, as the last button in the cluster: the
 * verdict pair keeps the positions it has always had, and unlike those two
 * this stays available after the suggestion has been decided.
 */
function SuggestionJumpButton({ onClick }: { onClick: () => void }): JSX.Element {
  const { classes, cx } = useButtonStyles();

  return (
    <AlineTooltip arrow placement="left" title="Scroll to location">
      <div className={classes.iconButtonClusterWrap}>
        <button
          aria-label="Scroll to location"
          className={cx(classes.iconButton, classes.iconButtonClustered, classes.iconButtonJump)}
          type="button"
          onClick={event => {
            event.stopPropagation();
            onClick();
          }}
        >
          <Crosshair className={classes.icon} />
        </button>
      </div>
    </AlineTooltip>
  );
}

/**
 * Renders one suggestion's generation-time preview cards document-style:
 * visual diffs for in-paragraph edits, inserted HTML as a suggested
 * insert (blue underline), removed HTML struck through, notes and errors
 * as plain text. The cards are what the run generated — deliberately not
 * recomputed against the live document, so a stale suggestion still shows
 * exactly what was proposed (ADR 0005).
 */
function PlaybookRedlineCards({ cards }: { cards: CodeBlockRedlineItem[] }): JSX.Element {
  const { classes } = useStyles();
  const theme = useTheme<Theme>();

  return (
    <>
      {cards.map((card, index) => {
        switch (card.type) {
          case 'redline-edit':
            return (
              <React.Fragment key={`${card.locationAnchor}-${index}`}>
                {card.actions.map(action => (
                  // No "Change:" heading and no inner card box — the unit
                  // already labels and frames the diff.
                  <RedlineActionVisualDiff
                    key={action.groupId}
                    plain
                    title={null}
                    visualDiffSegments={action.visualDiffSegments}
                  />
                ))}
              </React.Fragment>
            );
          case 'redline-insert':
            return <PlaybookInsertPreview key={card.groupId} card={card} />;
          case 'redline-remove':
            return (
              <div
                key={card.groupId}
                className={classes.previewHtml}
                style={suggestTextStyles(theme.appColors, 'remove')}
              >
                {stripHtml(card.html)}
              </div>
            );
          case 'redline-note':
            return (
              <Typography key={card.groupId} className={classes.previewHtml}>
                {card.text}
              </Typography>
            );
          case 'redline-edit-error':
            return (
              <Typography key={`${card.groupId}-${index}`} color="error" variant="caption">
                One suggested edit could not be prepared: {card.message}
              </Typography>
            );
        }
      })}
    </>
  );
}

/**
 * An insertion with the reference paragraph it is anchored to, in document
 * order: the paragraph above the new content when the content goes after it,
 * below when the content goes before it, so the preview reads as the document
 * will. The paragraph arrives already trimmed to a readable length, with an
 * ellipsis on whichever side was cut.
 */
function PlaybookInsertPreview({ card }: { card: CodeBlockRedlineInsert }): JSX.Element {
  const { classes, cx } = useStyles();
  const theme = useTheme<Theme>();
  const insertedText = (
    <div className={classes.previewHtml} style={suggestTextStyles(theme.appColors, 'insert')}>
      {stripHtml(card.html)}
    </div>
  );

  if (card.anchorPreview === undefined) return insertedText;

  const anchor = (
    <div className={cx(classes.previewHtml, classes.previewAnchor)}>{card.anchorPreview}</div>
  );

  if (card.position === 'after') {
    return (
      <>
        {anchor}
        {insertedText}
      </>
    );
  }

  return (
    <>
      {insertedText}
      {anchor}
    </>
  );
}

/** Preview text without markup; the document-styled render is the editor's job. */
function stripHtml(html: string): string {
  const container = document.createElement('div');
  container.innerHTML = html;
  return container.textContent ?? '';
}

/**
 * The per-suggestion review wiring a position offers while its rule is open
 * for review. Absent on read-through renders (weaker fallbacks, the manual
 * block, resolved-elsewhere positions render the units without controls).
 */
export interface SuggestionReviewProps {
  decisions: ProposalDecisions;
  /** Sibling-position lock or an existing resolution: no new decisions. */
  disabled: boolean;
  /**
   * The whole position went stale: the document changed under one of its
   * suggestions, and a proposal's suggestions stand or fall together. Shows
   * the banner above the suggestions and freezes accepts; rejecting stays
   * possible, since it never touches the document.
   */
  isStale: boolean;
  onAccept: (suggestion: ProposalSuggestion) => void;
  onReject: (suggestion: ProposalSuggestion) => void;
  onRerun: () => void;
  onUnreject: (suggestion: ProposalSuggestion) => void;
}

function getSuggestionNavigationTarget(
  cards: CodeBlockRedlineItem[]
): { documentId: string; locationAnchor: string } | null {
  for (const card of cards) {
    if (card.type === 'redline-note') continue;
    return { documentId: card.documentId, locationAnchor: card.locationAnchor };
  }
  return null;
}

/**
 * One suggestion as a reviewable unit (ADR 0006): its preview cards with the
 * review controls for its state. After a decision the same Accept/Reject
 * cluster stays; the chosen action is selected and the other is disabled.
 */
function PlaybookSuggestionUnit({
  dimmed = false,
  review,
  suggestion,
}: {
  review: SuggestionReviewProps | null;
  suggestion: ProposalSuggestion;
  dimmed?: boolean;
}): JSX.Element {
  const { classes, cx } = useStyles();
  const { navigateToCardAnchor } = usePlaybookNavigate();
  const decision = review?.decisions[suggestion.id];
  const isReviewable = getIsSuggestionReviewable(suggestion);
  const navigationTarget = getSuggestionNavigationTarget(suggestion.previewCards);
  const isDecided = decision === 'accepted' || decision === 'rejected';
  const canJump = Boolean(navigationTarget) && !isDecided && !dimmed;

  // Notes keep the stacked title-above-body layout; only reviewable units
  // use the diff | actions row from the review mock.
  if (!isReviewable) {
    return (
      <div className={classes.suggestionSection}>
        <div className={classes.suggestionNoteBody}>
          <span className={classes.suggestionTitle}>AI Note</span>
          <PlaybookRedlineCards cards={suggestion.previewCards} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cx(classes.suggestionSection, canJump && classes.suggestionSectionNavigable)}
      onClick={() => {
        if (navigationTarget && canJump) navigateToCardAnchor(navigationTarget);
      }}
    >
      <div className={classes.suggestionRow}>
        <div className={classes.suggestionBody}>
          <PlaybookRedlineCards cards={suggestion.previewCards} />
        </div>
        {Boolean(review || canJump) && (
          <div className={classes.suggestionActions}>
            {review !== null && (
              <>
                <SuggestionReviewButton
                  clustered
                  disabled={review.disabled || review.isStale || isDecided}
                  icon="accept"
                  selected={decision === 'accepted'}
                  tooltip="Accept suggestion"
                  onClick={() => review.onAccept(suggestion)}
                />
                <SuggestionReviewButton
                  clustered
                  disabled={review.disabled || isDecided}
                  icon="reject"
                  selected={decision === 'rejected'}
                  tooltip="Reject suggestion"
                  onClick={() => review.onReject(suggestion)}
                />
              </>
            )}
            {canJump && navigationTarget !== null && (
              <SuggestionJumpButton onClick={() => navigateToCardAnchor(navigationTarget)} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A proposal's suggestions as reviewable units. Note suggestions (the AI
 * explaining why no edit was possible) are labeled as notes and carry no
 * controls — they never count toward review completion.
 */
export function PlaybookSuggestionList({
  dimmed = false,
  review,
  suggestions,
}: {
  review: SuggestionReviewProps | null;
  suggestions: ProposalSuggestion[];
  dimmed?: boolean;
}): JSX.Element {
  const { classes } = useStyles();
  return (
    <div className={classes.suggestionList}>
      {suggestions.map(suggestion => (
        <PlaybookSuggestionUnit
          key={suggestion.id}
          dimmed={dimmed}
          review={review}
          suggestion={suggestion}
        />
      ))}
    </div>
  );
}

export interface PlaybookPositionBlockProps {
  chip: { label: string; tone: PlaybookChipTone } | null;
  label: string;
  suggestions: ProposalSuggestion[] | null;
  text: string;
  children?: React.ReactNode;
  /**
   * Skipped fallbacks, and evaluated positions that were not the resolution,
   * render faded so the used position is the one that reads as active.
   */
  dimmed?: boolean;
  /** The run's verdict reasoning — markdown with pre-validated citations. */
  explanation?: string | null;
  /** Per-suggestion review wiring; null renders the units read-only. */
  suggestionReview?: SuggestionReviewProps | null;
}

export function PlaybookPositionBlock({
  children,
  chip,
  dimmed = false,
  explanation = null,
  label,
  suggestionReview = null,
  suggestions,
  text,
}: PlaybookPositionBlockProps): JSX.Element {
  const { classes, cx } = useStyles();
  const hasPreview = Boolean(suggestions?.length);
  const hasReviewableSuggestions = Boolean(suggestions?.some(getIsSuggestionReviewable));
  const endsWithSuggestions = hasPreview && !children;

  return (
    <div
      className={cx(
        classes.block,
        dimmed && classes.blockDimmed,
        endsWithSuggestions && classes.blockFlushBottom
      )}
    >
      <div className={classes.blockInset}>
        <div className={classes.labelRow}>
          <span className={classes.label}>{label}</span>
          {chip && <PlaybookChip label={chip.label} tone={chip.tone} />}
        </div>
        <Typography className={classes.positionText}>{text}</Typography>
        {explanation && (
          <>
            <span className={classes.reasoningHeading}>
              <Sparkles size={14} />
              AI Reasoning
            </span>
            <Typography className={classes.text} component="div">
              <PlaybookExplanationMarkdown markdown={explanation} />
            </Typography>
          </>
        )}
        {hasReviewableSuggestions && (
          <>
            <div className={classes.sectionHeadingRow}>
              <span className={classes.sectionHeading}>Suggested edits</span>
            </div>
            {suggestionReview && suggestionReview.isStale && (
              <div className={classes.staleBanner}>
                <span>The document changed since this run</span>
                <button
                  className={classes.staleRerun}
                  type="button"
                  onClick={suggestionReview.onRerun}
                >
                  <RefreshCw size={12} /> Rerun
                </button>
              </div>
            )}
          </>
        )}
      </div>
      {hasPreview && suggestions && (
        <PlaybookSuggestionList
          dimmed={dimmed}
          review={suggestionReview}
          suggestions={suggestions}
        />
      )}
      {Boolean(children) && (
        <div className={cx(classes.blockInset, classes.actionsRow, classes.actionsRowButtons)}>
          {children}
        </div>
      )}
    </div>
  );
}
